import { useState, useEffect, useCallback, useRef } from 'react';
import { MarketplaceService } from '../services/marketplaceService';
import toast from 'react-hot-toast';

export const useMarketplace = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const latestRequest = useRef(0);
  
  const fetchAssets = useCallback(async (filters = {}) => {
    const request = ++latestRequest.current;
    setLoading(true);
    try {
      let data = await MarketplaceService.getAssets(filters);
      const allAssets = Array.isArray(data) ? data : [...(data?.results || [])];
      const visited = new Set();
      while (data?.next) {
        const query = new URL(data.next, window.location.origin).search;
        if (visited.has(query)) throw new Error('Repeated marketplace page');
        visited.add(query);
        data = await MarketplaceService.getAssets({ ...filters, ...Object.fromEntries(new URLSearchParams(query)) });
        if (request !== latestRequest.current) return;
        allAssets.push(...(Array.isArray(data) ? data : data?.results || []));
      }
      if (request === latestRequest.current) setAssets(allAssets);
    } catch (error) {
      if (request !== latestRequest.current) return;
      console.error('Failed to fetch assets:', error);
      toast.error('Failed to fetch marketplace assets');
      setAssets([]);
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, []);
  
  const createAsset = async (assetData) => {
    try {
      const newAsset = await MarketplaceService.createAsset(assetData);
      toast.success('Asset listed successfully!');
      await fetchAssets();
      return newAsset;
    } catch (error) {
      console.error('Failed to create asset:', error);
      toast.error(error.response?.data?.error || 'Failed to create asset');
      throw error;
    }
  };
  
  const updateAsset = async (assetId, assetData) => {
    try {
      const updatedAsset = await MarketplaceService.updateAsset(assetId, assetData);
      toast.success('Asset updated successfully!');
      await fetchAssets();
      return updatedAsset;
    } catch (error) {
      console.error('Failed to update asset:', error);
      toast.error(error.response?.data?.error || 'Failed to update asset');
      throw error;
    }
  };
  
  const deleteAsset = async (assetId) => {
    try {
      await MarketplaceService.deleteAsset(assetId);
      toast.success('Asset deleted successfully');
      await fetchAssets();
    } catch (error) {
      console.error('Failed to delete asset:', error);
      toast.error('Failed to delete asset');
      throw error;
    }
  };
  
  return { assets, loading, fetchAssets, createAsset, updateAsset, deleteAsset };
};

export const useMarketplaceOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await MarketplaceService.getOrders();
      // Handle paginated response
      if (data && data.results && Array.isArray(data.results)) {
        setOrders(data.results);
      } else if (Array.isArray(data)) {
        setOrders(data);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to fetch marketplace orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const createOrder = async (orderData) => {
    try {
      const order = await MarketplaceService.createOrder(orderData);
      toast.success('Order created! BTC1 locked in escrow');
      return order;
    } catch (error) {
      console.error('Failed to create order:', error);
      console.error('Error response:', error.response?.data);
      
      // Extract and format error message
      let errorMessage = 'Failed to create order';
      
      if (error.response?.data?.error) {
        const backendError = error.response.data.error;
        console.log('Backend error message:', backendError);
        
        // Check for specific error types
        if (backendError.includes('NotEnoughFunds') || backendError.includes('not_enough_funds') || 
            backendError.toLowerCase().includes('insufficient')) {
          errorMessage = '❌ Insufficient BTC1 balance! Please deposit BTC1 to your wallet first.';
        } else if (backendError.includes('escrow')) {
          errorMessage = '❌ Failed to lock BTC1 in escrow. Please check your balance and try again.';
        } else {
          errorMessage = backendError;
        }
      } else if (error.response?.data) {
        // Try to extract any error message from response data
        errorMessage = JSON.stringify(error.response.data);
      }
      
      console.log('Displaying error toast:', errorMessage);
      toast.error(errorMessage, {
        duration: 5000,
        style: {
          background: '#1f2937',
          color: '#fff',
        },
      });
      throw error;
    }
  };
  
  const sellerConfirmDelivery = async (orderId, notes = '') => {
    try {
      const order = await MarketplaceService.sellerConfirmDelivery(orderId, notes);
      toast.success('Delivery confirmed!');
      await fetchOrders();
      return order;
    } catch (error) {
      console.error('Failed to confirm delivery:', error);
      toast.error(error.response?.data?.error || 'Failed to confirm delivery');
      throw error;
    }
  };
  
  const buyerConfirmReceipt = async (orderId, notes = '') => {
    try {
      const order = await MarketplaceService.buyerConfirmReceipt(orderId, notes);
      toast.success('Receipt confirmed! Waiting for admin approval');
      await fetchOrders();
      return order;
    } catch (error) {
      console.error('Failed to confirm receipt:', error);
      toast.error(error.response?.data?.error || 'Failed to confirm receipt');
      throw error;
    }
  };
  
  const cancelOrder = async (orderId) => {
    try {
      await MarketplaceService.cancelOrder(orderId);
      toast.success('Order cancelled. BTC1 refunded.');
      await fetchOrders();
    } catch (error) {
      console.error('Failed to cancel order:', error);
      toast.error(error.response?.data?.error || 'Failed to cancel order');
      throw error;
    }
  };
  
  return { 
    orders, 
    loading, 
    fetchOrders, 
    createOrder, 
    sellerConfirmDelivery,
    buyerConfirmReceipt,
    cancelOrder
  };
};

export const useMarketplaceAsset = (assetId) => {
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const fetchAsset = useCallback(async () => {
    if (!assetId) return;
    
    setLoading(true);
    try {
      const data = await MarketplaceService.getAsset(assetId);
      setAsset(data);
    } catch (error) {
      console.error('Failed to fetch asset:', error);
      toast.error('Failed to fetch asset details');
      setAsset(null);
    } finally {
      setLoading(false);
    }
  }, [assetId]);
  
  useEffect(() => {
    fetchAsset();
  }, [fetchAsset]);
  
  return { asset, loading, refetch: fetchAsset };
};

export const useMarketplaceOrder = (orderId) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    
    setLoading(true);
    try {
      const data = await MarketplaceService.getOrder(orderId);
      setOrder(data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to fetch order details');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);
  
  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);
  
  return { order, loading, refetch: fetchOrder };
};

